import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike'
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus'
import LocalParkingIcon from '@mui/icons-material/LocalParking'
import {
    Box,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Switch,
    Typography,
} from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useContext, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { LayerSpecification } from 'maplibre-gl'
import AutomaticSheet from 'src/components/sheet/AutomaticSheet'
import { MapRenderContext } from 'src/contexts/MapRenderContext'

interface FilterItem {
    name: string
    id: string
}

interface FilterGroup {
    name: string
    icon: ReactNode
    handler: (id: string, isOn: boolean) => void
    items: FilterItem[]
}

export const Route = createFileRoute('/filter')({
    component: FilterComponent,
})

function FilterComponent() {
    const { t } = useTranslation()
    const { map } = useContext(MapRenderContext)

    const localStorageKey = 'mapFilterOptions'

    const publicTransportHandler = (id: string, isOn: boolean) => {
        id = id.toLowerCase()
        map?.current?.getLayer(`stops-circles-${id}`)?.setLayoutProperty('visibility', isOn ? 'visible' : 'none')
        map?.current?.getLayer(`stops-symbol-${id}`)?.setLayoutProperty('visibility', isOn ? 'visible' : 'none')
        console.log(
            'id:', id,
            'isOn:', isOn,
            'stops-circles:', map?.current?.getLayer(`stops-circles-${id}`),
            'stops-symbol:', map?.current?.getLayer(`stops-symbol-${id}`),
        )
        // force redraw
        map?.current?.redraw()
    }

    const micromobilityHandler = (id: string, isOn: boolean) => {
        const layers = map?.current?.getStyle().layers.filter((layer: LayerSpecification) => layer.id.includes(id)) ?? []
        layers.forEach((layer: LayerSpecification) => {
            map?.current?.setLayoutProperty(layer.id, 'visibility', isOn ? 'visible' : 'none')
            map?.current?.redraw()
        })
    }

    const parkingHandler = (id: string, isOn: boolean) => {
        map?.current?.getLayer('parkings-circles')?.setLayoutProperty('visibility', isOn ? 'visible' : 'none')
        map?.current?.redraw()
    }

    const options = [
        {
            name: t('Public transport'),
            icon: <DirectionsBusIcon />,
            handler: publicTransportHandler,
            items: [
                { name: 'LPP', id: 'LPP' },
                { name: 'IJPP', id: 'IJPP' },
                { name: 'Marprom', id: 'MARPROM' },
                { name: 'Celebus', id: 'CELEBUS' },
                { name: 'MP Kranj', id: 'MOKranj' },
                { name: 'MP Krško', id: 'MOKrsko' },
                { name: 'MP Novo mesto', id: 'MONM' },
                { name: 'MP Brežice', id: 'Brezice' },
                { name: 'MP Sevnica', id: 'Sevnica' },
                { name: 'MP Velenje', id: 'MOVelenje' },
                { name: 'MP Murska Sobota', id: 'MOMS' },
                { name: 'MP Postojna', id: 'Postojna' },
                { name: t('Others'), id: 'unknown' },
            ],
        },
        {
            name: t('Micromobility'),
            icon: <DirectionsBikeIcon />,
            handler: micromobilityHandler,
            items: [
                { name: 'Bicikelj', id: 'jcdecaux_ljubljana' },
                { name: 'MBajk', id: 'jcdecaux_maribor' },
                { name: 'Nomago Bikes', id: 'nextbike' },
                { name: 'SCBikes', id: 'scbikes' },
                { name: 'Avant2Go', id: 'avant2go' },
                { name: 'Share\'ngo', id: 'sharengo' },
                { name: 'GreenGo', id: 'greengo' },
                { name: 'Kvik', id: 'kvik' },
            ],
        },
        {
            name: t('Parking'),
            icon: <LocalParkingIcon />,
            handler: parkingHandler,
            items: [{ name: 'Parking', id: 'parking' }],
        },
    ]

    const [checkedOptions, setCheckedOptions] = useState<Record<string, boolean>>(() => {
        const savedState = localStorage.getItem(localStorageKey)
        if (savedState) {
            return JSON.parse(savedState)
        }
        const initialState: Record<string, boolean> = {}
        options.forEach((group) => {
            group.items.forEach((item) => {
                initialState[item.id] = true // Default all to "on"
            })
        })
        return initialState
    })

    const handleToggle = (group: FilterGroup, item: FilterItem) => {
        setCheckedOptions((prev) => {
            const updatedState = { ...prev, [item.id]: !prev[item.id] }

            if (group.handler) {
                group.handler(item.id, !prev[item.id])
            }

            localStorage.setItem(localStorageKey, JSON.stringify(updatedState))
            return updatedState
        })
    }

    const handleGroupToggle = (group: FilterGroup) => {
        const allChecked = group.items.every((item: FilterItem) => checkedOptions[item.id])

        setCheckedOptions((prev) => {
            const updatedState = { ...prev }

            group.items.forEach((item: FilterItem) => {
                updatedState[item.id] = !allChecked

                if (group.handler) {
                    group.handler(item.id, !allChecked)
                }
            })

            localStorage.setItem(localStorageKey, JSON.stringify(updatedState))
            return updatedState
        })
    }

    return (
        <AutomaticSheet>
            <title>{t('Filters')}</title>
            <Box sx={{ padding: 2 }}>
                <Typography variant="h5">{t('Filters')}</Typography>
                <Divider />
                <List>
                    {options.map((group, groupIndex) => (
                        <div key={groupIndex}>
                            <ListItemButton onClick={() => handleGroupToggle(group)}>
                                <ListItemIcon>{group.icon}</ListItemIcon>
                                <ListItemText primary={group.name} sx={{ fontSize: 18 }} />
                                <Switch
                                    checked={group.items.every((item: FilterItem) => checkedOptions[item.id])}
                                    {...(group.items.some((item: FilterItem) => checkedOptions[item.id])
                                        && !group.items.every((item: FilterItem) => checkedOptions[item.id])
                                        ? { indeterminate: true }
                                        : {})}
                                />
                            </ListItemButton>
                            {group.items.map((item: FilterItem) => (
                                <ListItemButton
                                    key={item.id}
                                    onClick={() => handleToggle(group, item)}
                                >
                                    <ListItemText primary={item.name} />
                                    <Switch
                                        edge="start"
                                        checked={!!checkedOptions[item.id]}
                                        disableRipple
                                    />
                                </ListItemButton>
                            ))}
                            {groupIndex < options.length - 1 && <Divider />}
                        </div>
                    ))}
                </List>
            </Box>
        </AutomaticSheet>
    )
}

export default FilterComponent
